/* istanbul ignore file */

import React, { memo, useMemo } from 'react';

import { useSelector } from '@xstate5/react';

import { useResponsive } from '@sg/garnish';

import { useLocationSearchFlags } from '../../hooks/useLocationSearchFlags';
import { useLocationSearchVisibleLocations } from '../../hooks/useLocationSearchVisibleLocations';
import { type LocationSearchService } from '../../machine';
import { LocationResultsListsV2 } from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Renders location lists using the V2 layout with separate recent, nearby,
 * and standard location groups.
 */
export const LocationResults = memo((props: LocationResultsProps) => {
  const {
    locationSearchActorRef,
    variation = 'standard',
    interactedLocationRestaurantSlug,
    interactedLocationDeliveryAddressId,
    withSafeAreaInsets = false,
    onExpandedChange,
  } = props;

  const { minWidth } = useResponsive();

  const { isLoadingLocations } = useLocationSearchFlags({ locationSearchActorRef });

  const visibleLocations = useLocationSearchVisibleLocations({ locationSearchActorRef });

  // ─── Derived ─────────────────────────────────────────────────────────

  const selectedLocationSearchType = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.locationSearchType,
  );

  const deliveryLocation = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.deliveryLocation,
  );

  const activeLocations = useMemo(() => {
    const { pickup = [], outpost = [] } = visibleLocations ?? {};

    if (selectedLocationSearchType === 'delivery') {
      return deliveryLocation ? [deliveryLocation as unknown as (typeof pickup)[number]] : [];
    }

    return selectedLocationSearchType === 'outpost' ? outpost : pickup;
  }, [deliveryLocation, visibleLocations, selectedLocationSearchType]);

  const hasNoResults = !isLoadingLocations && activeLocations.length === 0;

  // ─────────────────────────────────────────────────────────────────────

  if (hasNoResults) return null;

  // ─── SM ──────────────────────────────────────────────────────────────

  if (minWidth.isSM) {
    return (
      <LocationResultsListsV2.SM
        variation={variation}
        locationSearchActorRef={locationSearchActorRef}
        interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
        interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
      />
    );
  }

  // ─── XS ──────────────────────────────────────────────────────────────

  return (
    <LocationResultsListsV2.XS
      variation={variation}
      locationSearchActorRef={locationSearchActorRef}
      interactedLocationRestaurantSlug={interactedLocationRestaurantSlug}
      interactedLocationDeliveryAddressId={interactedLocationDeliveryAddressId}
      withSafeAreaInsets={withSafeAreaInsets}
      onExpandedChange={onExpandedChange}
    />
  );
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsProps = {
  locationSearchActorRef: LocationSearchService; // prettier-ignore
  variation?: 'recent-and-nearby' | 'standard';
  withSafeAreaInsets?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
  interactedLocationRestaurantSlug?: string;
  interactedLocationDeliveryAddressId?: string;
};
